/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable @typescript-eslint/unbound-method */
import { type QueryClient } from '@tanstack/react-query';
import { type Middleware } from 'redux';
import {
  ADD_TO_CART,
  type ActionAddToCart,
  type ActionClearCheckout,
  type ActionClearEditOrder,
  type ActionInitExistingOrder,
  type ActionRemoveFromCart,
  type ActionSelectPaymentType,
  type ActionUpdateCart,
  type ActionUpdateEditOrder,
  CLEAR_CHECKOUT,
  CLEAR_EDIT_ORDER,
  INIT_EXISTING_ORDER,
  REMOVE_FROM_CART,
  SELECT_PAYMENT_TYPE,
  UPDATE_CART,
  UPDATE_EDIT_ORDER,
  clearCheckout,
  clearDeliveryWindows,
  fetchWindows,
  turnOnSplitPayment,
  updateOrder,
} from '~shared/actions/checkout';
// eslint-disable-next-line import/no-deprecated
import { fetchDeliveries } from '~shared/actions/deliveries';
// eslint-disable-next-line import/no-deprecated
import { autobillPrescription, fetchPrescriptions } from '~shared/actions/prescriptions';
import { getItemsInOrder, getPrescriptionsInOrder } from '~shared/features/checkout/selectors/getItems';
import { getOrder, getOrderAddressId } from '~shared/features/checkout/selectors/getOrder';
import { type Item } from '~shared/features/checkout/types';
import { deleteAddOnPrescription } from '~shared/features/essentials/actions';
import {
  type FetchNextAvailableDatesOptions,
  fetchCheckoutNextAvailableDates,
} from '~shared/features/next-available-date/queries/fetchCheckoutNextAvailableDates';
import { invalidateNextAvailableDatesQueries } from '~shared/features/next-available-date/queries/nextAvailableDatesQueryKeys';
import { getNextAvailableDateFacilityID } from '~shared/features/next-available-date/selectors/getNextAvailableDateFacilityId';
import getShipmentID from '~shared/features/next-available-date/selectors/getShipmentId';
import { queries } from '~shared/queries/query-keys';
import { type Prescription, type ReduxDispatch, type ReduxStateShared } from '~shared/types';

const allowedTypes = {
  [ADD_TO_CART]: true,
  [REMOVE_FROM_CART]: true,
  [UPDATE_EDIT_ORDER]: true,
  [UPDATE_CART]: true,
  [SELECT_PAYMENT_TYPE]: true,
  [INIT_EXISTING_ORDER]: true,
  [CLEAR_CHECKOUT]: true,
  [CLEAR_EDIT_ORDER]: true,
};

const ORDER_PRICING_UPDATE_CART_KEYS: Record<string, boolean> = {
  payment_method_id: true,
  second_payment_method_id: true,
  shipment_payment_methods: true,
  include_sharps_container: true,
  items: true,
  selected_payment_types: true,
  tip_amount: true,
  tip_payment_method_id: true,
  date: true,
  pay_with_apple_pay: true,
};

type Actions =
  | ActionAddToCart
  | ActionClearCheckout
  | ActionClearEditOrder
  | ActionInitExistingOrder
  | ActionUpdateCart
  | ActionUpdateEditOrder
  | ActionSelectPaymentType
  | ActionRemoveFromCart;

const cart =
  (queryClient: QueryClient): Middleware<unknown, ReduxStateShared, ReduxDispatch> =>
  ({ dispatch, getState }) =>
  (next) =>
  (action: Actions) => {
    const result = next(action);

    if (!allowedTypes[action.type]) {
      return result;
    }

    const state = getState();
    const prescriptions = getPrescriptionsInOrder(state);

    // Delivery windows are only for courier. Mail uses specific names (e.g. fedex_priority_overnight), not "mail".
    const isOrderCourierOnly = (deliveryMethod: string | undefined) => deliveryMethod === 'courier';

    switch (action.type) {
      case INIT_EXISTING_ORDER: {
        const order = action?.payload?.order;
        if ((order?.date || order?.address_id) && isOrderCourierOnly(order?.delivery_method)) {
          dispatch(fetchWindows(prescriptions?.map((rx) => rx.id)));
        }

        if (action?.payload?.order?.shipment_payment_methods?.length === 2) {
          dispatch(turnOnSplitPayment());
        }

        queryClient.invalidateQueries({ queryKey: queries.pricing.order._def });
        invalidateNextAvailableDatesQueries(queryClient);
        break;
      }
      case UPDATE_EDIT_ORDER:
      case UPDATE_CART: {
        const items = getItemsInOrder(state);
        if (!items.length) break;

        if (
          (action.payload.date || action.payload.address_id) &&
          isOrderCourierOnly(getOrder(state)?.delivery_method)
        ) {
          dispatch(fetchWindows(prescriptions?.map((rx) => rx.id)));
        }

        const payloadKeys = Object.keys(action.payload);
        const shouldInvalidatePricing = payloadKeys.some((key) => ORDER_PRICING_UPDATE_CART_KEYS[key]);
        if (shouldInvalidatePricing) {
          queryClient.invalidateQueries({ queryKey: queries.pricing.order._def });
        }

        const payloadRecord = action.payload as Record<string, unknown>;
        const shouldInvalidateNad =
          shouldInvalidatePricing || 'address_id' in payloadRecord || 'delivery_method' in payloadRecord;
        if (shouldInvalidateNad) {
          invalidateNextAvailableDatesQueries(queryClient);
        }

        break;
      }

      case ADD_TO_CART: {
        const { payload } = action;
        const { billing_overrides, resetOrderDate, skipNextAvailableDatesFetch, ...itemKey } = payload;

        const items = getItemsInOrder(state);
        const item = items.find((item: Item) => item.equals(itemKey));
        const prescription = item?.prescription;
        const prescriptions = getPrescriptionsInOrder(state);
        const addressID = getOrderAddressId(state);

        if (prescription) {
          // Cross-sell items are derived from unbundled products.
          // Only fetch dates for relevant prescriptions when we do not need to show cross-sell products
          const prescriptionIdsInOrder = prescriptions.map((rx: Prescription) => rx.id);
          const nextAvailableDateOptions: FetchNextAvailableDatesOptions = {
            unbundledPrescriptionIDs: [prescription.id],
            bundledPrescriptionIDs: prescriptionIdsInOrder.length > 1 ? prescriptionIdsInOrder : [],
            addressID,
          };

          const runAfterAddPrescriptionToCart = async () => {
            // eslint-disable-next-line import/no-deprecated
            await dispatch(autobillPrescription(prescription.id, billing_overrides));
            // keep order pricing in sync with autobilled results and cart items
            queryClient.invalidateQueries({ queryKey: queries.pricing.order._def });
            invalidateNextAvailableDatesQueries(queryClient);
            // The correct order is to refresh deliveries after autobill, before refreshing prescriptions.
            // Why? Because refreshing prescriptions includes logic to remove any prescriptions from cart that aren't
            // ready to schedule. (see useRemoveUnschedulablePrescriptionsFromCart).
            // A delivery will appear not ready to schedule if it's refreshed during autobill and not after.
            // Next-available dates can refresh in parallel with deliveries.
            const refreshDeliveriesThenPrescriptions = async () => {
              // eslint-disable-next-line import/no-deprecated
              await dispatch(fetchDeliveries());
              // eslint-disable-next-line import/no-deprecated
              dispatch(fetchPrescriptions());
            };

            const refreshNextAvailableDates = async () => {
              if (skipNextAvailableDatesFetch) {
                return;
              }
              const latestState = getState();
              await fetchCheckoutNextAvailableDates(queryClient, {
                ...nextAvailableDateOptions,
                facilityID: getNextAvailableDateFacilityID(latestState),
                shipmentID: getShipmentID(latestState),
              });
            };

            await Promise.all([refreshDeliveriesThenPrescriptions(), refreshNextAvailableDates()]);
            if (resetOrderDate) {
              // resetOrderDate is passed in for flows that are likely to update the next available date to an
              // earlier date. This is currently used for 'Get it sooner' flows, where patients can choose
              // different payment options as a way to get their medication sooner.
              // In these scenarios, we should reset the order date and have select date component
              // calculate the order date based on the new next available date (after cart + NAD refresh).
              dispatch(updateOrder({ date: undefined }));
            }
          };
          void runAfterAddPrescriptionToCart();
        }

        dispatch(fetchWindows(prescriptions.map((rx) => rx.id)));

        break;
      }

      case SELECT_PAYMENT_TYPE: {
        queryClient.invalidateQueries({ queryKey: queries.pricing.order._def });
        break;
      }

      case REMOVE_FROM_CART: {
        const { payload } = action;
        const { prescription, resource_type } = payload;

        if (resource_type === 'Prescription' && prescription?.is_add_on_otc) {
          // no op on failure:
          // Deleting the Add On prescription is for garbage collecting purposes on the backend. The client doesn't care about
          // the result of this endpoint, nor does any part of the UI rely on its loading/success/error.
          deleteAddOnPrescription(prescription?.id)?.catch(() => {});
        }

        const items = getItemsInOrder(state);

        if (items.length === 0) {
          dispatch(clearCheckout());
          invalidateNextAvailableDatesQueries(queryClient);
          break;
        }

        queryClient.invalidateQueries({ queryKey: queries.pricing.order._def });
        invalidateNextAvailableDatesQueries(queryClient);
        const prescription_ids: number[] = items.map((item: Item) => item.prescription.id);
        dispatch(fetchWindows(prescription_ids));

        break;
      }

      case CLEAR_EDIT_ORDER:
      case CLEAR_CHECKOUT: {
        dispatch(clearDeliveryWindows());
        invalidateNextAvailableDatesQueries(queryClient);
        break;
      }

      default:
    }

    return result;
  };

export default cart;
/* eslint-enable sonarjs/cognitive-complexity */
/* eslint-enable @typescript-eslint/unbound-method */
